// Source de vérité unique des formules d'abonnement (frontend).
//
// Toutes les pages qui affichent ou utilisent les formules doivent consommer
// ces valeurs : page publique (Tarifs, Inscription), espace Propriétaire,
// espace Agence et espace Super Admin. Cela évite toute contradiction de prix
// ou de limite entre les écrans (problème relevé lors de l'audit).
//
// Le jour où la base de données est branchée, cette table deviendra une table
// `formules` côté Supabase, sans devoir réécrire les écrans.

export type FormuleId = "gratuit" | "essentiel" | "professionnel" | "agence";

export type Periodicite = "mensuel" | "annuel";

/** `null` signifie « illimité ». */
export interface FormuleLimites {
  proprietaires: number | null;
  immeubles: number | null;
  biens: number | null;
  locataires: number | null;
  contrats: number | null;
  employes: number | null;
}

export type FormuleStatut = "active" | "desactivee";

export interface FormuleCatalogue {
  id: FormuleId;
  nom: string;
  tagline: string;
  prixMensuel: number;
  prixAnnuel: number;
  highlight: boolean;
  cta: string;
  statut: FormuleStatut;
  limites: FormuleLimites;
}

export const FORMULES_CATALOGUE: FormuleCatalogue[] = [
  {
    id: "gratuit",
    nom: "Gratuit",
    tagline: "Pour découvrir la plateforme",
    prixMensuel: 0,
    prixAnnuel: 0,
    highlight: false,
    cta: "Commencer",
    statut: "active",
    limites: { proprietaires: 1, immeubles: 1, biens: 5, locataires: 5, contrats: 5, employes: 1 },
  },
  {
    id: "essentiel",
    nom: "Essentiel",
    tagline: "Pour les propriétaires et petites agences actifs",
    prixMensuel: 15000,
    prixAnnuel: 162000,
    highlight: false,
    cta: "Choisir cette formule",
    statut: "active",
    limites: { proprietaires: 5, immeubles: 3, biens: 30, locataires: 40, contrats: 40, employes: 2 },
  },
  {
    id: "professionnel",
    nom: "Professionnel",
    tagline: "Pour les patrimoines importants",
    prixMensuel: 20000,
    prixAnnuel: 216000,
    highlight: true,
    cta: "Choisir cette formule",
    statut: "active",
    limites: { proprietaires: 10, immeubles: 5, biens: 90, locataires: null, contrats: null, employes: 5 },
  },
  {
    id: "agence",
    nom: "Agence",
    tagline: "Pour les agences immobilières",
    prixMensuel: 25000,
    prixAnnuel: 270000,
    highlight: false,
    cta: "Choisir cette formule",
    statut: "active",
    limites: { proprietaires: null, immeubles: null, biens: null, locataires: null, contrats: null, employes: null },
  },
];

export const LIMITE_KEYS: Array<keyof FormuleLimites> = [
  "proprietaires",
  "immeubles",
  "biens",
  "locataires",
  "contrats",
  "employes",
];

export const LIMITE_LABELS: Record<keyof FormuleLimites, string> = {
  proprietaires: "Propriétaires",
  immeubles: "Immeubles",
  biens: "Biens",
  locataires: "Locataires",
  contrats: "Contrats",
  employes: "Employés",
};

export function getFormuleConfig(id: string): FormuleCatalogue | undefined {
  return FORMULES_CATALOGUE.find((formule) => formule.id === id);
}

export function formatFormuleLimite(value: number | null): string {
  if (value === null) return "Illimité";
  return value === 0 ? "Non inclus" : String(value);
}

export function isFormuleIllimitee(value: number | null): boolean {
  return value === null;
}

/** Économie de la facturation annuelle (10 %) exprimée en pourcentage entier. */
export function remiseAnnuellePourcent(formule: FormuleCatalogue): number {
  if (formule.prixMensuel <= 0) return 0;
  const plein = formule.prixMensuel * 12;
  if (plein <= 0) return 0;
  return Math.round(((plein - formule.prixAnnuel) / plein) * 100);
}

/** Avantages affichés sur la page publique Tarifs (alignés sur les limites ci-dessus). */
export const PUBLIC_FORMULE_FEATURES: Record<FormuleId, string[]> = {
  gratuit: [
    "1 immeuble",
    "5 biens",
    "5 locataires",
    "5 contrats",
    "1 utilisateur",
    "Fonctionnalités essentielles",
  ],
  essentiel: [
    "Jusqu'à 3 immeubles",
    "Jusqu'à 30 biens",
    "Jusqu'à 40 locataires et 40 contrats",
    "Gestion des loyers et paiements",
    "Suivi des impayés et dépenses",
    "Documents et statistiques de base",
  ],
  professionnel: [
    "Jusqu'à 5 immeubles",
    "Jusqu'à 90 biens",
    "Locataires et contrats illimités",
    "Gestion de l'entretien et des prestataires",
    "Statistiques avancées et exports",
    "5 utilisateurs",
  ],
  agence: [
    "Immeubles et biens illimités",
    "Gestion multi-propriétaires",
    "Gestion des employés et autorisations",
    "Suivi multi-propriétaires et rapports",
    "Statistiques et rapports avancés",
    "Utilisateurs illimités",
  ],
};

export interface FormuleComparisonRow {
  label: string;
  values: Array<string | boolean>;
}

/** Lignes booléennes du comparatif (indépendantes des valeurs numériques). */
const FORMULE_COMPARISON_BOOLEAN_ROWS: FormuleComparisonRow[] = [
  { label: "Gestion des loyers et paiements", values: [true, true, true, true] },
  { label: "Suivi des impayés", values: [true, true, true, true] },
  { label: "Entretien et prestataires", values: [false, true, true, true] },
  { label: "Dépenses et documents", values: [false, true, true, true] },
  { label: "Statistiques avancées", values: [false, false, true, true] },
  { label: "Gestion multi-propriétaires", values: [false, false, false, true] },
  { label: "Employés et autorisations", values: [false, false, false, true] },
];

/**
 * Construit le tableau comparatif public à partir d'un catalogue donné.
 * Utilisé avec les formules réelles de Supabase afin que toute modification
 * de limite faite par le Super Admin soit reflétée sur la page publique Tarifs.
 */
export function buildFormuleComparisonRows(formules: FormuleCatalogue[]): FormuleComparisonRow[] {
  const valeur = (formule: FormuleCatalogue, key: keyof FormuleLimites): string => {
    const limite = formule.limites[key];
    return limite === null ? "Illimité" : String(limite);
  };
  return [
    { label: "Immeubles", values: formules.map((f) => valeur(f, "immeubles")) },
    { label: "Biens", values: formules.map((f) => valeur(f, "biens")) },
    { label: "Locataires", values: formules.map((f) => valeur(f, "locataires")) },
    { label: "Contrats", values: formules.map((f) => valeur(f, "contrats")) },
    { label: "Utilisateurs", values: formules.map((f) => valeur(f, "employes")) },
    ...FORMULE_COMPARISON_BOOLEAN_ROWS,
  ];
}

/** Comparatif de repli, dérivé du catalogue statique validé. */
export const FORMULE_COMPARISON_ROWS: FormuleComparisonRow[] = buildFormuleComparisonRows(FORMULES_CATALOGUE);